/**
 * Redux slice managing the user state, including account info,
 * consumption data, job lists, webhooks, and API credentials.
 *
 * ## What is Redux?
 * Redux is a global state manager for the app. Instead of passing data
 * down through many React components via props, data is stored once in
 * a central "store" and any component can read or update it directly.
 *
 * A "slice" is one section of that store. This file defines:
 *   - The shape of the data (UserState)
 *   - The starting values (initialState)
 *   - The functions that update the data (reducers / actions)
 *
 * To update a value from anywhere in the app:
 *   store.dispatch(setUserInfo(newValue))
 *
 * To read a value inside a React component:
 *   const userInfo = useSelector((s) => s.user.userInfo)
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2024-2026
 */
import { SortDirection } from '@/components/IampTable'

import {
  ApiKey,
  CompanyConsumption,
  InvoiceInfo,
  JobDetail,
  Kpi,
  ServiceInfoMap,
  SubscriptionData,
  UserInfo,
  UserInfoComplete,
  Webhook,
  WebhookLog,
} from '@amplify3/duplo-back-end'
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'

/** A country available for billing, with its currency and EU membership. */
export interface Country {
  code: string
  name: string
  isEU: boolean
  currency: 'USD' | 'EUR'
}

/**
 * The full shape of the user section of the Redux store.
 */
export interface UserState {
  /** Cached list of AI Music Detector tasks. */
  aimdTaskList?: JobDetail[]
  /** Current sort direction for the AIMD task list. */
  aimdTaskListSortDirection: SortDirection
  /** Countries available for billing address selection. */
  allowedCountries: Country[]
  /** List of the company's API credentials (client apps). */
  apiKeyList?: ApiKey[]
  /** Company credit consumption for the current period. */
  companyConsumption?: CompanyConsumption
  /** Company KPI history (usage metrics per period). */
  companyKpi?: Kpi[]
  /** Invoice history and current subscription info. */
  invoiceInfo?: InvoiceInfo
  /** Cached list of jobs (tasks) for the job list view. */
  jobList?: JobDetail[]
  /** Current sort direction for the job list. */
  jobListSortDirection: SortDirection
  /** Pagination cursor: ID of the last fetched AIMD task. */
  lastAimdTaskId?: string
  /** Pagination cursor: ID of the last fetched job. */
  lastJobId?: string
  /** Pagination cursor: ID of the last fetched webhook log. */
  lastWebhookLogId?: string
  /** List of prices */
  priceList?: SubscriptionData
  /** Available services and their metadata (names, pricing, etc.). */
  servicesInfo?: ServiceInfoMap
  /** Basic info of the authenticated user (name, plan, etc.). */
  userInfo?: UserInfo
  /** Extended user info loaded separately (detailed account data). */
  userInfoComplete?: UserInfoComplete
  /** Webhooks configured by the company. */
  webhookList?: Webhook[]
  /** Call log entries for the selected webhook. */
  webhookLogList: WebhookLog[]
  /** Current sort direction for the webhook log list. */
  webhookLogListSortDirection: SortDirection
}

/**
 * Default values for the store when the app starts or after a logout.
 * Lists are empty, optional data is undefined (not yet loaded).
 */
export const initialState: UserState = {
  aimdTaskList: undefined,
  aimdTaskListSortDirection: SortDirection.DESCENDING,
  allowedCountries: [],
  apiKeyList: undefined,
  companyConsumption: undefined,
  companyKpi: undefined,
  invoiceInfo: undefined,
  jobList: undefined,
  jobListSortDirection: SortDirection.DESCENDING,
  lastAimdTaskId: undefined,
  lastJobId: undefined,
  lastWebhookLogId: undefined,
  priceList: undefined,
  servicesInfo: undefined,
  userInfo: undefined,
  webhookList: undefined,
  webhookLogList: [],
  webhookLogListSortDirection: SortDirection.DESCENDING,
}

/**
 * The Redux slice for user data.
 *
 * Each reducer is a function that updates one field in the store.
 * Calling store.dispatch(setXxx(value)) triggers the matching reducer,
 * which writes the new value and notifies all subscribed components.
 */
export const UserSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    /** Reset the entire user state back to its initial values (e.g. on logout). */
    resetUserSlice: () => initialState,
    /** Replace the cached AIMD task list. */
    setAimdTaskList: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.aimdTaskList>
    ) => {
      state.aimdTaskList = action.payload
    },
    /** Change the sort direction of the AIMD task list. */
    setAimdTaskListSortDirection: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.aimdTaskListSortDirection>
    ) => {
      state.aimdTaskListSortDirection = action.payload
    },
    /** Replace the list of countries available for billing. */
    setAllowedCountries: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.allowedCountries>
    ) => {
      state.allowedCountries = action.payload
    },
    /** Replace the list of API credentials (client apps). */
    setApiKeyList: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.apiKeyList>
    ) => {
      state.apiKeyList = action.payload
    },
    /** Replace the company's credit consumption data. */
    setCompanyConsumption: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.companyConsumption>
    ) => {
      state.companyConsumption = action.payload
    },
    /** Replace the company KPI history. */
    setCompanyKpi: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.companyKpi>
    ) => {
      state.companyKpi = action.payload
    },
    /** Replace the invoice and subscription info. */
    setInvoiceInfo: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.invoiceInfo>
    ) => {
      state.invoiceInfo = action.payload
    },
    /** Replace the cached job list. */
    setJobList: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.jobList>
    ) => {
      state.jobList = action.payload
    },
    /** Change the sort direction of the job list. */
    setJobListSortDirection: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.jobListSortDirection>
    ) => {
      state.jobListSortDirection = action.payload
    },
    /** Update the pagination cursor for the AIMD task list. */
    setLastAimdTaskId: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.lastAimdTaskId>
    ) => {
      state.lastAimdTaskId = action.payload
    },
    /** Update the pagination cursor for the job list. */
    setLastJobId: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.lastJobId>
    ) => {
      state.lastJobId = action.payload
    },
    /** Update the pagination cursor for the webhook log list. */
    setLastWebhookLogId: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.lastWebhookLogId>
    ) => {
      state.lastWebhookLogId = action.payload
    },
    /** Replace the list of prices. */
    setPriceList: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.priceList>
    ) => {
      state.priceList = action.payload
    },
    /** Replace the available services metadata. */
    setServicesInfo: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.servicesInfo>
    ) => {
      state.servicesInfo = action.payload
    },
    /** Replace the authenticated user's basic info. */
    setUserInfo: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.userInfo>
    ) => {
      state.userInfo = action.payload
    },
    /** Replace the authenticated user's extended info. */
    setUserInfoComplete: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.userInfoComplete>
    ) => {
      state.userInfoComplete = action.payload
    },
    /** Replace the list of webhooks configured by the company. */
    setWebhookList: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhookList>
    ) => {
      state.webhookList = action.payload
    },
    /** Replace the webhook call log entries. */
    setWebhookLogs: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhookLogList>
    ) => {
      state.webhookLogList = action.payload
    },
    /** Change the sort direction of the webhook log list. */
    setWebhookLogsSortDirection: (
      state: Draft<typeof initialState>,
      action: PayloadAction<typeof initialState.webhookLogListSortDirection>
    ) => {
      state.webhookLogListSortDirection = action.payload
    },
  },
})

/**
 * Selector to read the full user state from the store.
 * Usage in a component: const userState = useSelector(getUserState)
 */
export const getUserState = (state: { user: UserState }) => state.user

/**
 * The individual action creators, exported so any file can dispatch them.
 * Usage: store.dispatch(setUserInfo(newValue))
 */
export const {
  resetUserSlice,
  setAimdTaskList,
  setAimdTaskListSortDirection,
  setAllowedCountries,
  setApiKeyList,
  setCompanyConsumption,
  setCompanyKpi,
  setInvoiceInfo,
  setJobList,
  setJobListSortDirection,
  setLastAimdTaskId,
  setLastJobId,
  setLastWebhookLogId,
  setPriceList,
  setServicesInfo,
  setUserInfo,
  setUserInfoComplete,
  setWebhookList,
  setWebhookLogs,
  setWebhookLogsSortDirection,
} = UserSlice.actions

export default UserSlice.reducer
